import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { useContent } from "@/lib/content-context";
import { Coffee, FileText, BookOpen, Mail, Image, Palette, ArrowRight } from "lucide-react";

export default function AdminDashboard() {
    const { user } = useAuth();
    const { content } = useContent();
    const [stats, setStats] = useState({ blog: 0, contacts: 0, partners: 0, newsletter: 0, files: 0 });

    useEffect(() => {
        (async () => {
            try {
                const [b, m, f] = await Promise.all([
                    api.get("/admin/blog"),
                    api.get("/admin/messages"),
                    api.get("/admin/files"),
                ]);
                setStats({
                    blog: b.data.length,
                    contacts: m.data.contacts.length,
                    partners: m.data.partners.length,
                    newsletter: m.data.newsletter.length,
                    files: f.data.length,
                });
            } catch (e) {
                console.error(e);
            }
        })();
    }, []);

    const quick = [
        { to: "/admin/home", label: "Edit Home Page", desc: "Hero, story, food, community", icon: Coffee, testid: "quick-home" },
        { to: "/admin/pages", label: "Edit Other Pages", desc: "About, Partner, Contact", icon: FileText, testid: "quick-pages" },
        { to: "/admin/blog", label: "Manage Blog", desc: `${stats.blog} posts published`, icon: BookOpen, testid: "quick-blog" },
        { to: "/admin/media", label: "Media Library", desc: `${stats.files} files uploaded`, icon: Image, testid: "quick-media" },
        { to: "/admin/theme", label: "Theme & Brand", desc: "Colors, fonts, identity", icon: Palette, testid: "quick-theme" },
        { to: "/admin/messages", label: "Inbox", desc: `${stats.contacts + stats.partners} new messages`, icon: Mail, testid: "quick-messages" },
    ];

    return (
        <div data-testid="admin-dashboard">
            <div className="mb-12">
                <div className="tracked text-coffee/50 text-[10px] mb-3">Dashboard</div>
                <h1 className="font-editorial font-bold text-coffee text-4xl md:text-5xl leading-tight">
                    Welcome back, {user?.name || "owner"}.
                </h1>
                <p className="font-body text-coffee/60 mt-3">
                    Edit content, upload media, watch the inbox.
                </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-12">
                <Stat label="Blog Posts" value={stats.blog} />
                <Stat label="Contact Msgs" value={stats.contacts} />
                <Stat label="Partner Reqs" value={stats.partners} />
                <Stat label="Subscribers" value={stats.newsletter} />
                <Stat label="Media Files" value={stats.files} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {quick.map((q) => (
                    <Link
                        key={q.to}
                        to={q.to}
                        data-testid={q.testid}
                        className="group p-7 bg-white border border-coffee/10 hover:border-gold transition-colors flex flex-col"
                    >
                        <q.icon size={22} strokeWidth={1.3} className="text-gold mb-5" />
                        <div className="font-editorial font-bold text-2xl text-coffee">{q.label}</div>
                        <div className="font-body text-coffee/60 text-sm mt-2">{q.desc}</div>
                        <div className="mt-5 tracked text-[10px] text-coffee gold-underline inline-flex items-center gap-1 self-start">
                            Open <ArrowRight size={12} />
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="bg-coffee text-cream p-5">
            <div className="font-editorial font-bold text-3xl">{value}</div>
            <div className="tracked text-gold/80 text-[10px] mt-1">{label}</div>
        </div>
    );
}
